import React from 'react'

import { LocalStorageKeys } from '~config/constants'
import { useLocalStorage } from '~utils/hooks/use-local-storage'

type PinnedPagesContextType = {
    pinnedPages: string[]
    isPinned: (to: string | undefined) => boolean
    pinPage: (to: string) => void
    unpinPage: (to: string) => void
    movePinnedPage: (fromIndex: number, toIndex: number) => void
}

const PinnedPagesContext = React.createContext<PinnedPagesContextType>({
    pinnedPages: [],
    isPinned: () => false,
    pinPage: () => {},
    unpinPage: () => {},
    movePinnedPage: () => {},
})

export function usePinnedPages() {
    return React.useContext(PinnedPagesContext)
}

export default function PinnedPagesProvider({
    children,
}: React.PropsWithChildren) {
    return (
        <PinnedPagesContext.Provider value={usePinnedPagesValues()}>
            {children}
        </PinnedPagesContext.Provider>
    )
}

/**
 * Pages the user pinned to the Pinned submenu, stored as an ordered array of
 * routes. Array order is the display order (drag-and-drop reorders it), so
 * newly pinned pages append to the end.
 */
function usePinnedPagesValues(): PinnedPagesContextType {
    const [pinnedPages, setPinnedPages] = useLocalStorage<string[]>(
        LocalStorageKeys.pinnedSidebarPages,
        [],
    )

    const isPinned = (to: string | undefined) =>
        !!to && pinnedPages.includes(to)

    const pinPage = (to: string) => {
        setPinnedPages(prev => (prev.includes(to) ? prev : [...prev, to]))
    }

    const unpinPage = (to: string) => {
        setPinnedPages(prev => prev.filter(pinned => pinned !== to))
    }

    const movePinnedPage = (fromIndex: number, toIndex: number) => {
        setPinnedPages(prev => {
            if (
                fromIndex === toIndex ||
                fromIndex < 0 ||
                toIndex < 0 ||
                fromIndex >= prev.length ||
                toIndex >= prev.length
            ) {
                return prev
            }
            const next = [...prev]
            const [moved] = next.splice(fromIndex, 1)
            next.splice(toIndex, 0, moved)
            return next
        })
    }

    return { pinnedPages, isPinned, pinPage, unpinPage, movePinnedPage }
}
